import React from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  FormatBold as FormatBoldIcon,
  FormatClear as FormatClearIcon
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';

type Props = {
  value: string;
  disabled?: boolean;
  modified?: boolean;
  highlightBg?: string;
  editBorder?: string;
  /** Open directly in the Monaco source view (e.g. when the field was just
   *  upgraded from plain text mid-edit and the user expects to keep typing). */
  initialViewCode?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const RichTextInspectorField: React.FC<Props> = ({
  value,
  disabled = false,
  modified = false,
  highlightBg,
  editBorder,
  initialViewCode = false,
  onChange,
  onFocus,
  onBlur
}) => {
  const muiTheme = useTheme();
  const { rc } = useGlobalConfig();
  // Default view is a read-only rendered preview so the field never mutates its own
  // HTML via the browser's DOM engine — actual edits only ever happen in the Monaco
  // source view, so the value round-trips as the exact string the user typed.
  const [viewCode, setViewCode] = React.useState(initialViewCode);
  // Captured once at mount: whether this field arrived via mid-edit upgrade
  // (used by onMount to focus Monaco; a ref so the [] useCallback never goes stale).
  const initialViewCodeRef = React.useRef(initialViewCode);
  // Fire TEMPLATE_TEXT_EDITED once per edit session, not on every keystroke.
  const textEditedTrackedRef = React.useRef(false);

  const trackTextEditedOnce = React.useCallback(() => {
    if (textEditedTrackedRef.current) return;
    textEditedTrackedRef.current = true;
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONTENT.TEMPLATE_TEXT_EDITED);
  }, [rc]);

  // Stable refs so onMount closure never goes stale when parent re-renders
  const onFocusRef = React.useRef(onFocus);
  const onBlurRef = React.useRef(onBlur);
  React.useLayoutEffect(() => { onFocusRef.current = onFocus; }, [onFocus]);
  React.useLayoutEffect(() => { onBlurRef.current = onBlur; }, [onBlur]);

  const handleMonacoChange = React.useCallback((val: string | undefined) => {
    trackTextEditedOnce();
    onChange(val ?? '');
  }, [onChange, trackTextEditedOnce]);

  type MonacoEditor = Parameters<NonNullable<React.ComponentProps<typeof Editor>['onMount']>>[0];
  const editorRef = React.useRef<MonacoEditor | null>(null);

  const handleMonacoMount = React.useCallback((editor: MonacoEditor) => {
    editorRef.current = editor;
    editor.onDidFocusEditorWidget(() => onFocusRef.current?.());
    editor.onDidBlurEditorWidget(() => onBlurRef.current?.());
    // Field was just upgraded from plain text mid-edit — put the cursor at the
    // end and focus so the user can keep typing without re-clicking.
    if (initialViewCodeRef.current) {
      const model = editor.getModel();
      if (model) {
        const lastLine = model.getLineCount();
        editor.setPosition({ lineNumber: lastLine, column: model.getLineMaxColumn(lastLine) });
      }
      editor.focus();
    }
    // NOTE: deliberately no formatDocument here. Auto-formatting on mount mutated
    // the editor text while its onChange was suppressed, so the editor and the
    // parent's stored value diverged — first keystrokes could be silently lost,
    // and any later edit dragged formatting whitespace into the saved value,
    // flagging the field "modified" without a semantic change. The value must
    // always display exactly as stored.
  }, []);

  // Apply a text transform to the current Monaco selection (or the whole value if
  // nothing is selected) and write it back via executeEdits so undo/redo stays intact.
  const applySelectionEdit = React.useCallback((transform: (text: string) => string) => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!editor || !model) return;
    const selection = editor.getSelection();
    const range = selection && !selection.isEmpty() ? selection : model.getFullModelRange();
    const text = model.getValueInRange(range);
    const next = transform(text);
    if (next === text) return;
    editor.executeEdits('rich-text-format', [{ range, text: next }]);
    editor.focus();
  }, []);

  const execBold = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    applySelectionEdit(text => {
      const alreadyBold = /^<strong>[\s\S]*<\/strong>$/i.test(text);
      return alreadyBold ? text.replace(/^<strong>([\s\S]*)<\/strong>$/i, '$1') : `<strong>${text}</strong>`;
    });
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONTENT.TEMPLATE_FORMATTING_APPLIED, { formatting_type: 'bold' });
  }, [applySelectionEdit, rc]);

  const execClearFormat = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    applySelectionEdit(text => text.replace(/<[^>]+>/g, ''));
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONTENT.TEMPLATE_FORMATTING_APPLIED, { formatting_type: 'clear' });
  }, [applySelectionEdit, rc]);

  const monacoOptions = React.useMemo(() => ({
    readOnly: disabled,
    minimap: { enabled: false },
    lineNumbers: 'off' as const,
    wordWrap: 'on' as const,
    scrollBeyondLastLine: false,
    fontSize: 12,
    automaticLayout: true,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    folding: false,
    lineDecorationsWidth: 4,
    lineNumbersMinChars: 0,
    glyphMargin: false,
    renderLineHighlight: 'none' as const,
    hover: { enabled: false },
    padding: { top: 8, bottom: 8 },
    scrollbar: { vertical: 'auto' as const, horizontal: 'auto' as const, alwaysConsumeMouseWheel: false },
  }), [disabled]);

  const borderColor = modified ? editBorder || 'warning.main' : 'divider';
  const borderWidth = modified ? '2px' : '1px';

  return (
    <Box
      sx={{
      width: '100%',
        border: '1px solid',
        borderColor,
        borderWidth,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: modified ? highlightBg : 'background.paper',
        ...(disabled && { bgcolor: 'action.disabledBackground' })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 0.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: 1 }}>
          {viewCode && (
            <>
              <Tooltip title="Bold">
                <span>
                  <IconButton size="small" disabled={disabled} onMouseDown={execBold} sx={{ color: 'text.secondary' }}>
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear formatting">
                <span>
                  <IconButton size="small" disabled={disabled} onMouseDown={execClearFormat} sx={{ color: 'text.secondary' }}>
                    <FormatClearIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
        <Tooltip title={viewCode ? 'Preview' : 'View HTML'}>
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setViewCode(v => !v)}
              sx={{ p: 0.25, color: viewCode ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}
            >
              {viewCode ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <CodeIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {viewCode ? (
        <Editor
          height="180px"
          defaultLanguage="html"
          value={value}
          theme={muiTheme.palette.mode === 'dark' ? 'vs-dark' : 'vs'}
          loading={null}
          onChange={handleMonacoChange}
          onMount={handleMonacoMount}
          options={monacoOptions}
        />
      ) : (
        // Read-only rendered preview — never editable, so the browser's DOM engine
        // never gets a chance to rewrite/mangle the stored HTML on this path.
        <Box
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: value }}
          sx={{
            px: 1.5,
            py: 1,
            minHeight: 72,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
            cursor: disabled ? 'not-allowed' : 'default',
            '& p': { my: 0 },
            '& p + p': { mt: 1 }
          }}
        />
      )}
    </Box>
  );
};

export default RichTextInspectorField;
